import { PAGE_LIMIT_MAX, errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SearchField } from "@/components/app/patterns/search-field";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { searchPatch, optionalSearch } from "@/lib/search";
import { orpc } from "@/lib/orpc";
import { switchSession } from "@/lib/session/session";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { cn } from "@/lib/utils";
import { PlatformCreateOrganizationDialog } from "./-components/platform-create-organization-dialog";
import { PlatformEditOrganizationDialog } from "./-components/platform-edit-organization-dialog";
import { PlatformActivationRequestsCard } from "./-components/platform-activation-requests-card";
import { PlatformEnterDialog } from "./-components/platform-enter-dialog";
import { PlatformTwoFactorNotice } from "./-components/platform-two-factor-notice";
import {
  PlatformOrganizationsTable,
  type PlatformOrganization,
} from "./-components/platform-organizations-table";

export const Route = createFileRoute("/_authenticated/platform")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: optionalSearch(search.q),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-console"),
  component: PlatformConsole,
});

/** Tenant's front door: every customer organization on this deployment. */
function PlatformConsole() {
  const { q } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const term = q ?? "";
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PlatformOrganization | null>(null);
  const [entering, setEntering] = useState<PlatformOrganization | null>(null);
  const canManage = useCan("platform:manage");
  const queryClient = useQueryClient();

  const list = useQuery({
    ...orpc.administration.organizations.list.queryOptions({
      input: { search: q || undefined, limit: PAGE_LIMIT_MAX, offset: 0 },
    }),
    placeholderData: keepPreviousData,
  });
  const plans = useQuery(orpc.administration.organizations.plans.queryOptions());

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.administration.organizations.key() });

  const create = useMutation(
    orpc.administration.organizations.create.mutationOptions({
      onSuccess: async (organization) => {
        toast.success(`${organization.name} is ready with its first admin.`);
        setCreating(false);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const update = useMutation(
    orpc.administration.organizations.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization updated");
        setEditing(null);
        await invalidate();
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const enter = useMutation(
    orpc.administration.organizations.enter.mutationOptions({
      onSuccess: () => {
        setEntering(null);
        switchSession(queryClient, "/");
      },
      onError: (error) => toast.error(errorWords(error)),
    }),
  );

  const rows = list.data?.rows ?? [];
  const searching = list.isPlaceholderData || settling;
  const active = rows.filter((row) => row.status === "active").length;
  const facilities = rows.reduce((total, row) => total + row.facilityCount, 0);

  return (
    <>
      <PlatformTwoFactorNotice />
      <PageHeader
        title="Organizations"
        meta={
          list.isPending
            ? "Loading…"
            : list.isError
              ? undefined
              : searching
                ? "Searching…"
                : `${rows.length} organization${rows.length === 1 ? "" : "s"}, ${active} active, ${facilities} facilities in total`
        }
        actions={
          canManage ? <Button onClick={() => setCreating(true)}>Create organization</Button> : null
        }
      />

      <SearchField
        id="organization-search"
        value={search}
        onChange={setSearch}
        placeholder="Search organizations"
      />
      <output className="sr-only">
        {list.isError || !term
          ? ""
          : searching
            ? "Searching…"
            : rows.length === 0
              ? `No organization matches “${term}”`
              : `${rows.length} organization${rows.length === 1 ? "" : "s"} found`}
      </output>

      {list.isPending ? (
        <Skeleton className="h-64 w-full rounded-xl" />
      ) : list.isError ? (
        <ReadFailed
          message="Couldn't load organizations."
          error={list.error}
          onRetry={() => void list.refetch()}
          retrying={list.isRefetching}
        />
      ) : searching && rows.length === 0 ? (
        <Skeleton aria-busy="true" className="h-64 w-full rounded-xl" />
      ) : rows.length === 0 && term ? (
        <EmptyState
          title={`No organization matches “${term}”`}
          action={
            <Button
              variant="outline"
              onClick={() => {
                setSearch("");
                document.getElementById("organization-search")?.focus();
              }}
            >
              Clear search
            </Button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No organizations yet"
          description="Organizations appear here once the first tenant is created."
        />
      ) : (
        <div aria-busy={searching || undefined} className={cn(searching && "opacity-60")}>
          <PlatformOrganizationsTable
            organizations={rows}
            canManage={canManage}
            entering={enter.isPending}
            onEdit={setEditing}
            onEnter={(organizationId) =>
              setEntering(rows.find((row) => row.id === organizationId) ?? null)
            }
          />
        </div>
      )}

      <PlatformActivationRequestsCard />

      <PlatformEnterDialog
        organizationName={entering?.name ?? null}
        pending={enter.isPending}
        onSubmit={(reason) =>
          entering ? enter.mutate({ organizationId: entering.id, reason }) : null
        }
        onClose={() => setEntering(null)}
      />

      <PlatformCreateOrganizationDialog
        open={creating}
        onOpenChange={setCreating}
        pending={create.isPending}
        onSubmit={(values) => create.mutate(values)}
      />

      {editing ? (
        <PlatformEditOrganizationDialog
          organization={rows.find((row) => row.id === editing.id) ?? editing}
          plans={plans.data ?? []}
          pending={update.isPending}
          onClose={() => setEditing(null)}
          onSubmit={(values) => update.mutate(values)}
        />
      ) : null}
    </>
  );
}
